import { message } from '@repo/ui';
import { type NodeProps } from '@xyflow/react';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type IfElseNodeType, nodeSchemas } from 'automations/types/nodeTypes';
import { saveConditionNode } from 'automations/utils/conditionNode';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ZodError } from 'zod';

import AutomationsCriteriaBuilder from '../AutomationsCriteriaBuilder';
import AdditionalHandlers from './AdditionalHandlers';

export default function IfElseNodeAdditionalContent({
  node,
}: {
  node: NodeProps<IfElseNodeType>;
}) {
  const { id } = node;
  const dispatch = useAutomationDispatch();
  const parentId = useSelector(getParentIdSelector(id));
  const { open, setMenuOpen, contentMode } = useCommonNodeHandles(node);

  const handleSave = useCallback(async (criteriaId: number | undefined) => {
    try {
      const parsedNodeData = nodeSchemas.IfElseNodeData.parse({
        ...node.data,
        additionalOptions: {
          criteriaGroupId: criteriaId,
        },
      });
      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));
      await dispatch(saveConditionNode(node.id, parentId, contentMode));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? 'Failed to update automation';
      message.error(errorMessage);
    }
  }, [dispatch, node.data, node.id, parentId, setMenuOpen, contentMode]);

  return (
    <>
      <AdditionalHandlers node={node} />
      <AutomationsCriteriaBuilder
        title="Condition - If / Else"
        open={open}
        onClose={() => setMenuOpen(false)}
        onSave={handleSave}
        existingCriteriaId={node.data.additionalOptions?.criteriaGroupId}
        saveButtonText="Set Condition"
        allowEmpty={false}
      />
    </>
  );
}
